import Router from 'next/router';
import posthog from 'posthog-js';
import * as React from 'react';

const SUPPORT_EMAIL = 'support@easy-peasy.ai';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  errorId: string | null;
  componentStack: string | null;
}

function generateErrorId() {
  return `err_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

// Detects errors caused by a stale HTML page referencing chunks from an older
// deploy whose URLs no longer exist on the new build. The fix is a full page
// reload so the user fetches the fresh HTML with the new chunk hashes.
function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk \d+ failed|Failed to load chunk|Loading CSS chunk/i.test(
      error.message,
    )
  );
}

const CHUNK_RELOAD_KEY = 'app:chunkReloadCount';
const CHUNK_RELOAD_LIMIT = 2;
// How long the page must run without a chunk error before we consider the
// reload "successful" and reset the counter. Mounting alone is not enough —
// the boundary mounts before its children render, so a broken chunk can
// reject AFTER mount on every reload, which would clear the counter forever
// and trap the user in an infinite reload loop.
const CHUNK_RELOAD_RESET_DELAY_MS = 10_000;

class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, errorId: null, componentStack: null };
  private chunkResetTimer: ReturnType<typeof setTimeout> | null = null;

  private routeChangeHandler = () => {
    if (this.state.error) {
      this.setState({ error: null, errorId: null, componentStack: null });
    }
  };

  componentDidMount() {
    Router.events.on('routeChangeComplete', this.routeChangeHandler);
    // Reset the chunk-reload counter only after the page has run cleanly for
    // long enough that the previous reload is plausibly successful. If a
    // chunk error fires before this timeout, we'll keep the existing count
    // and eventually fall through to the error UI instead of looping.
    this.chunkResetTimer = setTimeout(() => {
      try {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      } catch {
        // sessionStorage may be unavailable (private mode, blocked storage)
      }
    }, CHUNK_RELOAD_RESET_DELAY_MS);
  }

  componentWillUnmount() {
    Router.events.off('routeChangeComplete', this.routeChangeHandler);
    if (this.chunkResetTimer !== null) {
      clearTimeout(this.chunkResetTimer);
      this.chunkResetTimer = null;
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, errorId: generateErrorId() };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const errorId = this.state.errorId ?? generateErrorId();
    const componentStack = info.componentStack ?? null;
    this.setState({ componentStack, errorId });

    const context = {
      errorId,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      buildSha: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? null,
      componentStack,
    };

    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, context);

    try {
      posthog.captureException(error, {
        $exception_source: 'react-error-boundary',
        ...context,
      });
    } catch {
      // posthog may not be initialized yet on very early errors
    }

    // Stale-deploy chunk error: silently reload to fetch fresh HTML + chunks.
    // Bounded by sessionStorage counter to prevent reload loops if the new
    // deploy is itself broken.
    if (isChunkLoadError(error) && typeof window !== 'undefined') {
      try {
        const count = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? '0');
        if (count < CHUNK_RELOAD_LIMIT) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, String(count + 1));
          window.location.reload();
        }
      } catch {
        // sessionStorage blocked — reload once, accept the risk of a loop
        window.location.reload();
      }
    }
  }

  reload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    const { error, errorId, componentStack } = this.state;
    if (!error) return this.props.children;

    const url = typeof window !== 'undefined' ? window.location.href : '';
    const buildSha = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? 'unknown';

    const debugInfo =
      `Error ID: ${errorId}\n` +
      `URL: ${url}\n` +
      `Build: ${buildSha}\n` +
      `Time: ${new Date().toISOString()}\n` +
      `Message: ${error.message}\n` +
      `Stack:\n${error.stack ?? '(no stack)'}\n` +
      (componentStack ? `\nComponent stack:${componentStack}\n` : '');

    const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      `App error ${errorId}`,
    )}&body=${encodeURIComponent(
      `Hi,\n\nI ran into an error using Easy-Peasy.AI. Details below:\n\n${debugInfo}\nWhat I was doing:\n(please describe)\n`,
    )}`;

    return (
      <div className='min-h-screen flex items-center justify-center bg-white dark:bg-slate-900 p-6'>
        <div className='max-w-xl w-full'>
          <div className='text-center'>
            <h1 className='text-2xl font-semibold text-gray-900 dark:text-white mb-2'>
              Something went wrong
            </h1>
            <p className='text-gray-600 dark:text-slate-400 mb-6'>
              We hit an unexpected error. Try reloading the page — if it keeps
              happening, please email us with the details below and we&apos;ll
              fix it.
            </p>
            <div className='flex justify-center gap-3 mb-6'>
              <button
                type='button'
                onClick={this.reload}
                className='rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500'
              >
                Reload page
              </button>
              <a
                href={mailto}
                className='rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800'
              >
                Email support
              </a>
            </div>
          </div>
          <details className='text-left'>
            <summary className='cursor-pointer text-sm text-gray-500 dark:text-slate-500'>
              Technical details (please include when emailing)
            </summary>
            <pre className='mt-3 max-h-72 overflow-auto rounded-md bg-gray-100 p-3 text-xs text-gray-800 dark:bg-slate-800 dark:text-slate-200 whitespace-pre-wrap break-words'>
              {debugInfo}
            </pre>
            <p className='mt-2 text-xs text-gray-500 dark:text-slate-500'>
              Error ID: <code>{errorId}</code>
            </p>
          </details>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
